import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

import { RestBuilder } from '../../services/helpers/rest.builder';
import { ISendChatMessageResponse, ISendChatMessageRequest } from '../../../../shared/interfaces/ai-chat.interface';
import { ApiHeaderOption } from '../../../../shared/constants/api';

export const THREE_MINUTES_MS: number = 180000; // 3 minutes default
export const TIMEOUT_DURATION: HttpContextToken<number> = new HttpContextToken<number>((): number => THREE_MINUTES_MS);

@Injectable({
  providedIn: 'root',
})
export class AiChatService {
  private readonly restBuilder: RestBuilder;

  constructor(private httpClient: HttpClient) {
    this.restBuilder = new RestBuilder();
  }

  private chat(id?: string): RestBuilder {
    return this.restBuilder.create().ai().chat(id);
  }

  public sendMessage(
    sendChatMessageRequest: ISendChatMessageRequest,
    timeoutMs: number = THREE_MINUTES_MS,
  ): Observable<ISendChatMessageResponse> {
    const context: HttpContext = new HttpContext().set(TIMEOUT_DURATION, timeoutMs);

    return this.httpClient
      .post<ISendChatMessageResponse>(this.chat().getApiUrl(), sendChatMessageRequest, {
        headers: { [ApiHeaderOption.skipLoader]: 'true' },
        context: context,
      })
      .pipe(
        timeout(timeoutMs),
        catchError((error: any) => {
          if (error.name === 'TimeoutError') {
            return throwError({
              status: 504, // Gateway Timeout
              message: 'ai_request_timed_out',
            });
          }
          return throwError(error);
        }),
      );
  }

  public setRating(id: string, rating: number): Observable<ISendChatMessageResponse> {
    const url: string = this.chat(id).rating().getApiUrl();

    return this.httpClient.put<ISendChatMessageResponse>(url, { rating });
  }

  public unsetRating(id: string): Observable<ISendChatMessageResponse> {
    const url: string = this.chat(id).rating().getApiUrl();

    return this.httpClient.delete<ISendChatMessageResponse>(url);
  }

  public getMessagesBySessionId(sessionId: string): Observable<ISendChatMessageResponse[]> {
    const url: string = this.chat().session(sessionId).getApiUrl();

    return this.httpClient.get<ISendChatMessageResponse[]>(url);
  }
}
